/**
 * Sorting a list by any column (MB's R11, 24 Oct 2026). Every column heading sorts when clicked, with its arrow; a
 * second click turns the order round; the choice is kept per screen. This is the rule the shared grid (decision 490:
 * sort, filter, column chooser) grows from, so a list never sorts in its own way.
 */
export type SortDir = 'asc' | 'desc';

export interface SortState {
  readonly column: string;
  readonly dir: SortDir;
}

/** What a column sorts by: a number, text, or several in turn (a date, then a flight number). */
export type SortValue =
  string | number | boolean | null | undefined | readonly (string | number | boolean | null | undefined)[];

type Scalar = string | number | boolean | null | undefined;

const collator = new Intl.Collator('en-AU', { numeric: true, sensitivity: 'base' });

function compareScalar(a: Scalar, b: Scalar): number {
  const ea = a === null || a === undefined || a === '';
  const eb = b === null || b === undefined || b === '';
  if (ea || eb) return ea === eb ? 0 : ea ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return collator.compare(String(a), String(b));
}

/** Ascending order of two values; an empty value (none, '') comes after every other. */
export function compareSortValues(a: SortValue, b: SortValue): number {
  const xs = Array.isArray(a) ? a : [a as Scalar];
  const ys = Array.isArray(b) ? b : [b as Scalar];
  for (let i = 0; i < Math.max(xs.length, ys.length); i++) {
    const c = compareScalar(xs[i], ys[i]);
    if (c !== 0) return c;
  }
  return 0;
}

const isEmpty = (v: SortValue): boolean =>
  v === null || v === undefined || v === '' || (Array.isArray(v) && (v.length === 0 || isEmpty(v[0])));

/** The rows in the column's order; equal rows keep their order, and rows with no value stay last either way. */
export function sortRows<T>(rows: readonly T[], value: (row: T) => SortValue, dir: SortDir): T[] {
  const keyed = rows.map((row, i) => ({ row, i, v: value(row) }));
  keyed.sort((a, b) => {
    const ea = isEmpty(a.v);
    const eb = isEmpty(b.v);
    if (ea !== eb) return ea ? 1 : -1;
    const c = compareSortValues(a.v, b.v);
    return (dir === 'asc' ? c : -c) || a.i - b.i;
  });
  return keyed.map((k) => k.row);
}

/** The order after a click on a heading: the sorted one turns round; another starts in its first direction. */
export function nextSort(current: SortState, column: string, firstDir: SortDir = 'asc'): SortState {
  if (current.column === column) return { column, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { column, dir: firstDir };
}

/** A stored order, when it names one of the list's columns; else the list's own default. */
export function parseSortState(
  raw: string | null,
  columns: readonly string[],
  fallback: SortState,
): SortState {
  if (!raw) return fallback;
  try {
    const v = JSON.parse(raw) as Partial<SortState> | null;
    if (
      v &&
      typeof v.column === 'string' &&
      columns.includes(v.column) &&
      (v.dir === 'asc' || v.dir === 'desc')
    )
      return { column: v.column, dir: v.dir };
  } catch {
    // not a stored order
  }
  return fallback;
}

/** The storage key of a screen's order. */
export function sortKey(app: 'field' | 'cloud', screen: string): string {
  return `teslyn.${app}.sort.v1.${screen}`;
}
